"use client";

import React, { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getErrorMessage } from "@/types/problem-detail";
import { ErrorMessage } from "@/components/ui/error-message";

export interface EntityActionsMenuProps {
  /** Accessibility label for the trigger (e.g. "Conversation actions") */
  ariaLabel: string;
  /** Applied to trigger wrapper for layout (e.g. ml-2 shrink-0) */
  className?: string;
  /** Dense trigger for list rows; default keeps 44px touch target for breadcrumbs. */
  compact?: boolean;
  /** Required when onDelete is used (passed to onDelete) */
  entityId?: string;
  /** Shown in delete confirmation dialog */
  entityTitle?: string;
  /** Used in dialog title (e.g. "Delete Conversation") */
  entityType?: string;
  /** When provided, show Delete menu item and confirm dialog */
  onDelete?: (id: string) => Promise<void>;
  /** Additional menu items rendered above Delete */
  children?: React.ReactNode;
}

/**
 * Shared "..." menu for entity actions (Delete and optional items).
 * Used in breadcrumb (conversation, experiment, learning) and in list cards.
 * Trigger has 44px min touch target for mobile; confirm dialog is responsive.
 */
export function EntityActionsMenu({
  ariaLabel,
  className,
  compact = false,
  entityId,
  entityTitle,
  entityType = "Item",
  onDelete,
  children,
}: EntityActionsMenuProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!entityId || !onDelete) return;
    setDeleteError(null);
    setIsDeleting(true);
    try {
      await onDelete(entityId);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      setDeleteError(getErrorMessage(error, "Failed to delete."));
    } finally {
      setIsDeleting(false);
    }
  };

  const showDelete = onDelete != null && entityId != null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size={compact ? "icon-xs" : "sm"}
            className={cn(
              compact
                ? "shrink-0"
                : "min-h-[44px] min-w-[44px] p-0 flex items-center justify-center",
              className
            )}
            aria-label={ariaLabel}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={4} className="max-h-[85vh] overflow-y-auto">
          {children}
          {showDelete && (
            <DropdownMenuItem
              onClick={handleDeleteClick}
              className="text-destructive focus:text-destructive"
            >
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isDeleteDialogOpen} onOpenChange={(open) => { setIsDeleteDialogOpen(open); if (!open) setDeleteError(null); }}>
        <DialogContent className="p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Delete {entityType}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{entityTitle ?? entityType}&quot;? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && <ErrorMessage message={deleteError} className="mb-2" />}
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              variant="destructive"
              className="w-full sm:w-auto"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
